import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Grid, Card, CardMedia, Chip, Stack,
  useTheme, useMediaQuery, alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import config from './config';
import { isBranchOpen } from './branchUtils';
import { useTranslation } from 'react-i18next';
import { useCart } from './CartContext';
import { useCurrency } from './CurrencyContext';
import { fetchRentalProducts } from './rentalApi';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import KeyIcon from '@mui/icons-material/Key';

const getImageUrls = (imageData) => {
  const fallbackUrl = '/logo-storesye.png';

  if (!imageData) {
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }

  if (typeof imageData === 'object' && !Array.isArray(imageData)) {
    if (imageData.thumbnail && imageData.medium && imageData.large) {
      return {
        thumbnail: imageData.thumbnail.startsWith('http')
          ? imageData.thumbnail
          : `${config.SITE_BASE_URL}${imageData.thumbnail}`,
        medium: imageData.medium.startsWith('http')
          ? imageData.medium
          : `${config.SITE_BASE_URL}${imageData.medium}`,
        large: imageData.large.startsWith('http')
          ? imageData.large
          : `${config.SITE_BASE_URL}${imageData.large}`
      };
    }
    return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
  }

  if (typeof imageData === 'string') {
    let fullUrl;
    if (imageData.startsWith('http')) {
      fullUrl = imageData;
    } else if (imageData.startsWith('data:image')) {
      fullUrl = imageData;
    } else if (imageData.startsWith('/')) {
      fullUrl = `${config.SITE_BASE_URL}${imageData}`;
    } else {
      fullUrl = imageData;
    }
    return { thumbnail: fullUrl, medium: fullUrl, large: fullUrl };
  }

  return { thumbnail: fallbackUrl, medium: fallbackUrl, large: fallbackUrl };
};

const PRICE_TYPE_LABELS = {
  hourly: { ar: '/ساعة', en: '/hr' },
  daily: { ar: '/يوم', en: '/day' },
  weekly: { ar: '/أسبوع', en: '/wk' },
  monthly: { ar: '/شهر', en: '/mo' },
};

export default function HomeRentalsPreview() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectedBranch } = useCart();
  const { selectedCurrency, getCurrencySymbol } = useCurrency();
  const [branchStatus, setBranchStatus] = useState({ isOpen: true, nextOpen: '' });
  const [rentalProducts, setRentalProducts] = useState([]);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isAr = i18n.language === 'ar';
  const currencySymbol = getCurrencySymbol(selectedCurrency);

  useEffect(() => {
    if (selectedBranch) {
      setBranchStatus(isBranchOpen(selectedBranch));
    }
  }, [selectedBranch]);

  useEffect(() => {
    const loadRentals = async () => {
      try {
        const data = await fetchRentalProducts(selectedBranch?.id, selectedCurrency);
        const products = Array.isArray(data) ? data : (data?.data || data?.Data || []);
        setRentalProducts(products.slice(0, 4));
      } catch (err) {
        console.error('Error fetching rental products for preview:', err);
        setRentalProducts([]);
      }
    };
    loadRentals();
  }, [selectedBranch, selectedCurrency]);

  if (!branchStatus.isOpen) return null;
  if (rentalProducts.length === 0) return null;

  return (
    <Box sx={{
      my: { xs: 3, sm: 4 },
      mx: { xs: 1.5, sm: 2 },
      p: { xs: 2, sm: 3 },
      bgcolor: theme.palette.background.paper,
      borderRadius: { xs: 3, sm: 4 },
      boxShadow: 1,
      border: '1px solid',
      borderColor: theme.palette.divider,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Subtle background accent */}
      <Box sx={{
        position: 'absolute', top: -40, right: isAr ? 'auto' : -40, left: isAr ? -40 : 'auto',
        width: 120, height: 120, borderRadius: '50%',
        bgcolor: alpha(theme.palette.primary.main, 0.05),
        pointerEvents: 'none',
      }} />

      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: { xs: 2, sm: 3 },
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <KeyIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
          </Box>
          <Typography
            variant="h5"
            fontWeight="bold"
            color={theme.palette.primary.main}
            sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
          >
            {isAr ? 'منتجات للتأجير' : 'Available for Rent'}
          </Typography>
        </Stack>
        <Chip
          icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
          label={isAr ? 'أسعار مرنة' : 'Flexible pricing'}
          size="small"
          variant="outlined"
          color="primary"
          sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' }, fontWeight: 600 }}
        />
      </Box>

      {/* Product Grid */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }}>
        {rentalProducts.map((item, index) => {
          const imageUrls = getImageUrls(item.ImageUrl);
          const priceLabel = PRICE_TYPE_LABELS[item.RentalPriceType] || { ar: '', en: '' };
          const rentalPrice = item.RentalPrice || item.Price || 0;

          return (
            <Grid item xs={6} sm={6} md={3} key={item.Id || index}>
              <Card sx={{
                position: 'relative',
                borderRadius: { xs: 2, sm: 3 },
                overflow: 'hidden',
                boxShadow: 2,
                bgcolor: theme.palette.background.paper,
                transition: 'all 0.3s',
                cursor: 'pointer',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-4px)',
                  boxShadow: isMobile ? 2 : 6,
                },
                '&:active': {
                  transform: isMobile ? 'scale(0.98)' : 'translateY(-4px)',
                }
              }}
                onClick={() => navigate('/rentals')}
              >
                <CardMedia
                  component="img"
                  height={isMobile ? '130' : '170'}
                  image={imageUrls.medium}
                  srcSet={`
                    ${imageUrls.thumbnail} 150w,
                    ${imageUrls.medium} 400w,
                    ${imageUrls.large} 1200w
                  `}
                  sizes="(max-width: 600px) 50vw, 25vw"
                  alt={item.ProductName}
                  loading="lazy"
                  sx={{ objectFit: 'cover' }}
                  onError={(e) => { e.target.src = '/logo-storesye.png'; }}
                />

                {/* Rental badge */}
                <Chip
                  icon={<KeyIcon sx={{ fontSize: 10, color: '#fff !important' }} />}
                  label={isAr ? 'تأجير' : 'Rent'}
                  size="small"
                  sx={{
                    position: 'absolute', top: 6, left: 6,
                    bgcolor: alpha(theme.palette.primary.main, 0.85),
                    color: '#fff', fontWeight: 700, fontSize: '0.55rem',
                    height: 20,
                    '& .MuiChip-icon': { color: '#fff' },
                    backdropFilter: 'blur(4px)',
                  }}
                />

                {/* Price badge */}
                <Box sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: `linear-gradient(transparent, ${alpha('#000', 0.7)})`,
                  p: 1, pt: 3,
                }}>
                  <Typography noWrap sx={{
                    color: '#fff', fontWeight: 700,
                    fontSize: { xs: '0.72rem', sm: '0.82rem' },
                    textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                    mb: 0.3,
                  }}>
                    {item.ProductName}
                  </Typography>
                  <Stack direction="row" spacing={0.5} alignItems="baseline">
                    <Typography sx={{
                      color: '#fff', fontWeight: 900,
                      fontSize: { xs: '0.85rem', sm: '1rem' },
                      textShadow: '0 1px 3px rgba(0,0,0,0.5)',
                    }}>
                      {Number(rentalPrice).toLocaleString()}
                    </Typography>
                    <Typography sx={{
                      color: alpha('#fff', 0.85), fontWeight: 600,
                      fontSize: { xs: '0.6rem', sm: '0.7rem' },
                    }}>
                      {currencySymbol}{isAr ? priceLabel.ar : priceLabel.en}
                    </Typography>
                  </Stack>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* CTA Button */}
      <Button
        fullWidth
        variant="contained"
        onClick={() => navigate('/rentals')}
        sx={{
          mt: { xs: 2, sm: 3 },
          borderRadius: { xs: 1.5, sm: 2 },
          color: theme.palette.custom?.white || '#fff',
          bgcolor: theme.palette.primary.main,
          py: { xs: 1.2, sm: 1.5 },
          fontSize: { xs: '0.85rem', sm: '1rem' },
          fontWeight: 'bold',
          transition: 'all 0.3s',
          '&:hover': {
            bgcolor: theme.palette.primary.dark,
            transform: isMobile ? 'none' : 'translateY(-2px)',
            boxShadow: isMobile ? 2 : 4,
          },
          '&:active': {
            transform: isMobile ? 'scale(0.98)' : 'translateY(-2px)',
          }
        }}
      >
        {isAr ? 'تصفح جميع منتجات التأجير' : 'Browse All Rental Products'}
      </Button>
    </Box>
  );
}